import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, PiggyBank, Scissors, Undo2 } from "lucide-react";
import { api, rp } from "@/lib/api";

const sel = "h-10 rounded-md border border-input bg-white px-3 text-sm";

// Laporan memakai bulan WIB (zona owner), bukan zona perangkat pembuka halaman.
const wibNow = () => new Date(Date.now() + (new Date().getTimezoneOffset() + 420) * 60000);

const Card = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-white border border-black/10 rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink-soft">
      {Icon && <Icon size={12} className="text-lm-red" />} {label}
    </div>
    <div className="mt-1 font-display text-2xl">{value}</div>
  </div>
);

export default function DepositReport() {
  const [month, setMonth] = useState(wibNow().toISOString().slice(0, 7));
  const [hubId, setHubId] = useState("");
  const [hubs, setHubs] = useState([]);
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/branches").then((r) => setHubs(r.data || [])).catch(() => setHubs([]));
  }, []);

  useEffect(() => {
    api.get("/reports/deposits", { params: { month, hub_id: hubId || undefined } })
      .then((r) => setD(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat laporan deposit"));
  }, [month, hubId]);

  const months = useMemo(() => {
    const out = [];
    const now = wibNow();
    for (let i = 0; i < 18; i += 1) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(dt.toISOString().slice(0, 7));
    }
    return out;
  }, []);

  const csv = `${process.env.REACT_APP_BACKEND_URL}/api/reports/deposits?month=${month}`
    + `${hubId ? `&hub_id=${hubId}` : ""}&format=csv`;
  const t = d?.totals || {};

  return (
    <div data-testid="deposit-report-page" className="pb-24">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Laporan Deposit</h1>
          <p className="mt-1 text-sm text-ink-soft max-w-2xl">
            Deposit yang masih ditahan, sudah direfund, dan dipotong — per Hub, per bulan.
            Deposit bukan pendapatan, jadi angka di sini tidak masuk omzet atau komisi.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select className={sel} data-testid="dep-month" value={month}
            onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className={sel} data-testid="dep-hub" value={hubId}
            onChange={(e) => setHubId(e.target.value)}>
            <option value="">Semua Hub</option>
            {hubs.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
          <a href={csv} data-testid="dep-export-csv"
            className="rounded-md border border-black/15 px-4 py-2.5 text-sm inline-flex items-center gap-1.5">
            <Download size={14} /> CSV
          </a>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Deposit ditahan" icon={PiggyBank} testid="dep-total-held"
          value={rp(t.held_amount || 0)} />
        <Card label="Direfund bulan ini" icon={Undo2} testid="dep-total-refunded"
          value={rp(t.refunded_amount || 0)} />
        <Card label="Dipotong bulan ini" icon={Scissors} testid="dep-total-deducted"
          value={rp(t.deducted_amount || 0)} />
        <Card label="Booking selesai" testid="dep-total-settled"
          value={`${t.settled_count || 0} / ${t.held_count || 0} ditahan`} />
      </div>

      <div className="mt-5 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm" data-testid="dep-hub-table">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3">Hub</th><th className="p-3">Ditahan</th><th className="p-3">Direfund</th>
              <th className="p-3">Dipotong</th><th className="p-3">Penuh / Sebagian / Ditahan</th>
              <th className="p-3">Rata-rata proses</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {(d?.hubs || []).map((h) => (
              <tr key={h.hub_id} data-testid={`dep-row-${h.code || h.hub_id}`}>
                <td className="p-3 font-medium">{h.name}</td>
                <td className="p-3">{rp(h.held_amount)}
                  <div className="text-[11px] text-ink-soft">{h.held_count} booking</div></td>
                <td className="p-3 text-emerald-700">{rp(h.refunded_amount)}</td>
                <td className={`p-3 ${h.deducted_amount ? "text-lm-red" : ""}`}>{rp(h.deducted_amount)}</td>
                <td className="p-3">{h.full_refunds} / {h.partial_refunds} / {h.forfeited}</td>
                <td className="p-3">{h.avg_settlement_days === null ? "—" : `${h.avg_settlement_days} hari`}</td>
              </tr>
            ))}
            {d && (d.hubs || []).length === 0 && (
              <tr><td colSpan={6} className="p-4 text-ink-soft">Belum ada Hub.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <div className="bg-white border border-black/10 rounded-lg p-5">
          <div className="text-sm font-semibold">Keputusan deposit bulan ini</div>
          <div className="mt-3 space-y-2 text-sm" data-testid="dep-settlements">
            {(d?.settlements || []).length === 0 && (
              <div className="text-xs text-ink-soft">Belum ada keputusan deposit di bulan ini.</div>
            )}
            {(d?.settlements || []).slice(0, 25).map((r) => (
              <div key={r.booking_id} className="border-b border-black/5 pb-2">
                <div className="flex justify-between gap-3">
                  <span className="font-medium">{r.booking_code}</span>
                  <span className="text-xs">{r.state_label}</span>
                </div>
                <div className="text-xs text-ink-soft">
                  {r.customer_name} · {r.hub_name} · {r.receipt_no || "-"}
                </div>
                <div className="text-xs mt-0.5">
                  Deposit {rp(r.deposit_amount)} · dipotong <span className={r.deducted ? "text-lm-red" : ""}>{rp(r.deducted)}</span>
                  {" "}· direfund <span className="text-emerald-700">{rp(r.refunded)}</span>
                </div>
                {r.reason && <div className="text-[11px] text-ink-soft mt-0.5">Alasan: {r.reason}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-black/10 rounded-lg p-5">
          <div className="text-sm font-semibold">Masih ditahan (tanggal sewa bulan ini)</div>
          <div className="mt-3 space-y-2 text-sm" data-testid="dep-outstanding">
            {(d?.outstanding || []).length === 0 && (
              <div className="text-xs text-ink-soft">Tidak ada deposit yang masih ditahan.</div>
            )}
            {(d?.outstanding || []).slice(0, 25).map((r) => (
              <div key={r.booking_id} className="flex justify-between gap-3 border-b border-black/5 pb-2">
                <div>
                  <div className="font-medium">{r.booking_code}</div>
                  <div className="text-xs text-ink-soft">
                    {r.customer_name} · {r.hub_name} · {r.rental_date}
                  </div>
                </div>
                <div className="text-right">
                  <div>{rp(r.deposit_amount)}</div>
                  <div className="text-[11px] text-ink-soft">{r.deposit_state || "Held"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {d?.note && <p className="mt-4 text-xs text-ink-soft" data-testid="dep-note">{d.note}</p>}
    </div>
  );
}
